import React, { useState } from 'react';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Typography,
    TextField,
    Box,
    Avatar,
    Chip,
    CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
    marginBottom: 0,
    boxShadow: 'none',
    borderBottom: 'none',
    borderRadius: 0,
}));
import { useSelector } from 'react-redux';
import {
    STATUS_IN_DRAFT,
    STATUS_PENDING_APPROVAL,
    MOVED_TO_DRAFT,
    STATUS_REQUISITION_APPROVED,
    STATUS_REQUISITION_REVIEW,
    REVIEW_APPROVED,
    REJECTED,
    POSITION_APPROVED,
} from '../../../constants';

const PositionComments = ({
    comments,
    status,
    isReview,
    addComment,
    isAddingComment,
    addCommentError,
    positionId,
    refetch,
    handleCommentAction,
    isDraft,
    isView,
    data,
}) => {
    const [reviewComment, setReviewComment] = useState('');
    const currentUser = useSelector((state) => state.session.user);

    const handleSubmitReview = async (action) => {
        if (!positionId) {
            return;
        }

        if (!reviewComment.trim()) {
            return;
        }

        try {
            const payload = { positionId, comment: { comment: reviewComment } };
            const res = await addComment(payload);

            if (res.data.status === 'SUCCESS') {
                refetch();
                setReviewComment('');
                handleCommentAction(action);
            } else {
                console.error('Error submitting comment:', res.data.message);
            }
        } catch (error) {
            console.error('Error submitting comment:', error);
        }
    };

    if (!isDraft && !isView) {
        if (
            !isReview ||
            status === STATUS_IN_DRAFT ||
            status === MOVED_TO_DRAFT ||
            status === STATUS_REQUISITION_APPROVED
        ) {
            return null;
        }
    }

    const isReviewer = data?.reviewPolicy?.reviewer?.id === parseInt(currentUser?.empDBID);
    const isApprover = data?.reviewPolicy?.approver?.id === parseInt(currentUser?.empDBID);
    const isReviewerOrApprover = isReviewer || isApprover;
    const displayAddComment = !isDraft && !isView && isReviewerOrApprover;
    const canComment =
        (isReviewer && status === STATUS_REQUISITION_REVIEW) || (isApprover && status === STATUS_PENDING_APPROVAL);

    return (
        <>
            {((displayAddComment && canComment) || comments?.length > 0) && (
                <StyledCard sx={{ mt: 4 }}>
                    <CardHeader
                        title={
                            <Typography variant="h5" component="h2">
                                Review Comments
                            </Typography>
                        }
                        subheader="Provide feedback on this position"
                    />
                    <CardContent>
                        {comments && comments.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                                    Previous Comments
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {comments.map((comment, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                bgcolor: 'grey.50',
                                                borderRadius: 2,
                                                p: 2,
                                                borderLeft: 4,
                                                borderColor: 'primary.main',
                                                position: 'relative',
                                            }}
                                        >
                                            {comment.positionStatus && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 12,
                                                        right: 12,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary">
                                                        Commented in
                                                    </Typography>
                                                    <Chip
                                                        label={comment.positionStatus}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: 'blue.50',
                                                            color: 'blue.800',
                                                            border: '1px solid',
                                                            borderColor: 'blue.200',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                </Box>
                                            )}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    justifyContent: 'space-between',
                                                    mb: 1,
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Avatar
                                                        sx={{
                                                            width: 32,
                                                            height: 32,
                                                            bgcolor: '#ffedd5',
                                                            color: 'primary.main',
                                                            fontSize: '0.875rem',
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {comment.commentedBy?.name?.charAt(0)?.toUpperCase() || 'U'}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {comment.commentedBy?.name || 'Unknown User'}
                                                        </Typography>
                                                        <Typography variant="caption">
                                                            {comment.commentedOn
                                                                ? new Date(comment.commentedOn).toLocaleDateString(
                                                                      'en-US',
                                                                      {
                                                                          year: 'numeric',
                                                                          month: 'short',
                                                                          day: 'numeric',
                                                                          hour: '2-digit',
                                                                          minute: '2-digit',
                                                                      },
                                                                  )
                                                                : 'Date not available'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                                                {comment.comment}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {displayAddComment && canComment ? (
                            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
                                <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 1 }}>
                                    Add New Comment
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    value={reviewComment}
                                    onChange={(e) => setReviewComment(e.target.value)}
                                    placeholder="Add your review comments here..."
                                    variant="outlined"
                                    sx={{ mb: 2 }}
                                />
                                {addCommentError && (
                                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                                        Error submitting comment: {addCommentError.message}
                                    </Typography>
                                )}

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                                    <Button
                                        onClick={() =>
                                            handleSubmitReview(
                                                isApprover ? POSITION_APPROVED : isReviewer ? REVIEW_APPROVED : null,
                                            )
                                        }
                                        variant="contained"
                                        disabled={isAddingComment || !reviewComment.trim()}
                                        sx={{
                                            bgcolor: 'success.main',
                                            '&:hover': { bgcolor: 'success.dark' },
                                        }}
                                        startIcon={isAddingComment ? <CircularProgress size={16} /> : null}
                                    >
                                        {isAddingComment ? 'Submitting...' : 'Approve'}
                                    </Button>
                                    <Button
                                        onClick={() => handleSubmitReview(REJECTED)}
                                        variant="contained"
                                        disabled={isAddingComment || !reviewComment.trim()}
                                        sx={{
                                            bgcolor: 'error.main',
                                            '&:hover': { bgcolor: 'error.dark' },
                                        }}
                                        startIcon={isAddingComment ? <CircularProgress size={16} /> : null}
                                    >
                                        {isAddingComment ? 'Submitting...' : 'Reject'}
                                    </Button>
                                </Box>
                            </Box>
                        ) : null}
                    </CardContent>
                </StyledCard>
            )}
        </>
    );
};

export default PositionComments;
